import "dayjs/locale/fr";
import { useEffect, useMemo, useRef, useState } from "react";
import { DeviceType, deviceType } from "expo-device";
import UAParser from "ua-parser-js";
import TextField from "../TextField";
import { TimeFieldProps } from "./TimeField";
import BaseField from "../BaseField";
import { Pressable, StyleSheet, TextInput } from "react-native";
import { getShadow, minutesAsDate, minutesAsTimeString } from "../../utils";
import { useFormatDateAsTimeString } from "./utils";
import { useTheme } from "../../hooks/use-theme";
import OutsidePressHandler from "react-native-outside-press";

const deviceInfo = new UAParser(window.navigator?.userAgent).getResult();

// always true except for Desktop Safari and Firefox
export const hasNativeTimePicker =
    deviceType !== DeviceType.DESKTOP ||
    !["Safari", "Firefox"].includes(deviceInfo.browser.name || "");

export const TimeField = ({
    value,
    disabled,
    onChange,
    isDuration = false,
    testID,
    style,
    label,
    placeholder,
    inputStyle,
    error,
    setTimeFn,
    popupFallback,
    ...props
}: TimeFieldProps) => {
    const formatDateAsTimeString = useFormatDateAsTimeString();
    const styles = useStyles();

    const inputRef = useRef<HTMLInputElement>(null);

    const [showTimePicker, setShowTimeChooser] = useState(false);
    const [time, setTime] = useState(value ? minutesAsTimeString(value) : "");

    useEffect(() => {
        if (showTimePicker) {
            inputRef.current?.showPicker();
            inputRef.current?.focus();
        }
    }, [showTimePicker]);

    useEffect(() => {
        const arr = time?.split(":");
        if (arr?.[1]?.length === 2) {
            const hours = Number.parseInt(arr[0]);
            const minutes = Number.parseInt(arr[1]);

            const totalMinutes = hours * 60 + minutes;
            if (value !== totalMinutes) {
                onChange(totalMinutes);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    useEffect(() => {
        if (setTimeFn) {
            const fnName = `set${setTimeFn}`;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window[fnName] = setTime;

            return () => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window[fnName] = null;
            };
        }
    }, [setTimeFn]);

    const baseFieldProps = useMemo(
        () => ({
            testID,
            style: [styles.baseField, style],
            inputStyle: [error ? styles.errorField : null, inputStyle],
            disabled,
            label,
            placeholder,
            error,
            isFocused: showTimePicker,
            ...props,
        }),
        [
            disabled,
            error,
            inputStyle,
            label,
            placeholder,
            props,
            showTimePicker,
            style,
            styles.baseField,
            styles.errorField,
            testID,
        ],
    );

    const textFieldProps = useMemo(
        () => ({
            isTime: true,
            onChangeText: setTime,
            ...baseFieldProps,
            value: time,
        }),
        [baseFieldProps, time],
    );

    const pressable = useMemo(
        () => (
            <Pressable
                id={testID}
                style={{
                    position: "absolute",
                    height: "100%",
                    width: "100%",
                    zIndex: 100,
                }}
                disabled={disabled}
                onPress={() => {
                    setShowTimeChooser(!showTimePicker);
                }}
            />
        ),
        [disabled, showTimePicker, testID],
    );

    return hasNativeTimePicker ? (
        <BaseField
            component={
                <>
                    {pressable}
                    <input
                        type="time"
                        id={testID}
                        onChange={(e) => {
                            if (e.target.value) {
                                setTime(e.target.value);
                            }
                        }}
                        style={{
                            position: "absolute",
                            height: "100%",
                            minWidth: "100%",
                            opacity: 0,
                        }}
                        onBlur={() => {
                            setShowTimeChooser(false);
                        }}
                        value={time}
                        ref={inputRef}
                    />
                </>
            }
            value={
                value !== undefined
                    ? formatDateAsTimeString(minutesAsDate(value), isDuration)
                    : undefined
            }
            {...baseFieldProps}
        />
    ) : popupFallback ? (
        <>
            <PopupTimeField
                isVisible={showTimePicker}
                onClose={() => {
                    setShowTimeChooser(false);
                }}
                {...textFieldProps}
            />
            {pressable}
        </>
    ) : (
        <TextField {...textFieldProps} />
    );
};

type PopupTimeFieldProps = {
    isVisible: boolean;
    onClose: () => void;
};

const PopupTimeField = ({ isVisible, onClose, ...props }: PopupTimeFieldProps) => {
    const styles = useStyles();
    const inputRef = useRef<TextInput>(null);

    useEffect(() => {
        if (isVisible) {
            inputRef.current?.focus();
        }
    }, [isVisible]);

    return isVisible ? (
        <OutsidePressHandler style={styles.popupContainer} onOutsidePress={onClose}>
            <TextField ref={inputRef} {...props} style={{ margin: 0 }} selectTextOnFocus />
        </OutsidePressHandler>
    ) : null;
};

const useStyles = () => {
    const { colors, roundness } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                popupContainer: {
                    position: "absolute",
                    top: 60,
                    zIndex: 10000,
                    borderRadius: roundness,
                    width: 100,
                    backgroundColor: colors.background,
                    ...getShadow({ level: 6 }),
                },
                baseField: {
                    maxWidth: 120,
                },
                errorField: {
                    color: colors.error,
                },
            }),
        [colors.background, colors.error, roundness],
    );
};
